<template>
  <section class="release-grid-section">
    <div class="container">
      <div v-if="title || subtitle" class="section-header" data-reveal="up">
        <h2 v-if="title" class="section-title">{{ title }}</h2>
        <p v-if="subtitle" class="section-subtitle">{{ subtitle }}</p>
      </div>

      <div class="release-grid" :class="`cols-${columns || 3}`" data-reveal="up">
        <div v-for="release in releases" :key="release._id" class="release-card">
          <div class="release-artwork">
            <img v-if="release.artwork?.url" :src="proxyUrl(release.artwork.url)" :alt="release.name" />
            <div v-else class="artwork-placeholder">
              <span>💿</span>
            </div>
          </div>
          <div class="release-info">
            <h3 class="release-name">{{ release.name }}</h3>
            <p class="release-artist">{{ release.displayArtist || 'Unknown Artist' }}</p>
          </div>
        </div>
      </div>

      <div v-if="!releases?.length" class="empty-state" data-reveal="up">
        <p>No releases to display.</p>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  title: String,
  subtitle: String,
  releases: { type: Array, default: () => [] },
  releaseIds: Array,
  columns: { type: Number, default: 3 }
})

function proxyUrl(url) {
  if (!url) return ''
  if (url.startsWith('http')) return `/api/assets/serve?url=${encodeURIComponent(url)}`
  return url
}
</script>

<style scoped>
.release-grid-section {
  padding: 100px 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-family: var(--font-heading);
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-main);
  margin-bottom: 12px;
}

.section-subtitle {
  color: var(--text-muted);
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
}

.release-grid {
  display: grid;
  gap: 24px;
}

.cols-2 { grid-template-columns: repeat(2, 1fr); }
.cols-3 { grid-template-columns: repeat(3, 1fr); }
.cols-4 { grid-template-columns: repeat(4, 1fr); }

.release-card {
  background: var(--surface-color);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all 0.3s ease;
}

.release-card:hover {
  border-color: var(--primary);
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

.release-artwork {
  aspect-ratio: 1;
  overflow: hidden;
}

.release-artwork img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.release-card:hover .release-artwork img {
  transform: scale(1.05);
}

.artwork-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-hover);
  font-size: 3rem;
}

.release-info {
  padding: 16px;
}

.release-name {
  font-weight: 700;
  font-size: 1rem;
  color: var(--text-main);
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.release-artist {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0;
}

.empty-state {
  text-align: center;
  color: var(--text-dim);
  padding: 40px;
}

@media (max-width: 768px) {
  .cols-3, .cols-4 { grid-template-columns: repeat(2, 1fr); }
  .section-title { font-size: 2rem; }
}

@media (max-width: 480px) {
  .cols-2, .cols-3, .cols-4 { grid-template-columns: 1fr; }
}
</style>
